import { useTranslation } from 'react-i18next';
import type { ComplianceReviewer } from '@shared/api/contracts/compliance';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  StatusBanner,
  Textarea,
} from '@shared/ui';

interface DiscrepancyAssignDialogProps {
  open: boolean;
  loading?: boolean;
  assignee: string;
  reason: string;
  assignees: ComplianceReviewer[];
  assigneesLoading?: boolean;
  assigneesError?: boolean;
  onOpenChange: (open: boolean) => void;
  onAssigneeChange: (value: string) => void;
  onReasonChange: (value: string) => void;
  onConfirm: () => void;
}

export function DiscrepancyAssignDialog({
  open,
  loading = false,
  assignee,
  reason,
  assignees,
  assigneesLoading = false,
  assigneesError = false,
  onOpenChange,
  onAssigneeChange,
  onReasonChange,
  onConfirm,
}: DiscrepancyAssignDialogProps) {
  const { t } = useTranslation('cabinet');
  const assigneeId = Number.parseInt(assignee.trim(), 10);
  const valid = Number.isSafeInteger(assigneeId) && assigneeId > 0 && reason.trim().length >= 3;
  const useSelect = assignees.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100%-2rem)] max-h-[calc(100dvh-2rem)] sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('reconciliation.case.assign.title')}</DialogTitle>
          <DialogDescription>{t('reconciliation.case.assign.description')}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="discrepancy-assignee">{t('reconciliation.case.assign.assignee')}</Label>
            {assigneesLoading ? <Skeleton className="h-10 w-full rounded-md" /> : null}
            {!assigneesLoading && assigneesError ? (
              <StatusBanner>{t('reconciliation.case.assign.loadError')}</StatusBanner>
            ) : null}
            {!assigneesLoading && useSelect ? (
              <Select value={assignee || undefined} onValueChange={onAssigneeChange}>
                <SelectTrigger id="discrepancy-assignee" className="w-full">
                  <SelectValue placeholder={t('reconciliation.case.assign.placeholder')} />
                </SelectTrigger>
                <SelectContent>
                  {assignees.map((admin) => (
                    <SelectItem key={admin.id} value={String(admin.id)}>
                      <span className="flex min-w-0 flex-col items-start gap-0 leading-tight">
                        <span className="truncate font-medium">{admin.email}</span>
                        <span className="text-xs text-muted-foreground">#{admin.id}</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : null}
            {!assigneesLoading && !useSelect ? (
              <>
                {assignees.length === 0 && !assigneesError ? (
                  <p className="text-xs text-muted-foreground">{t('reconciliation.case.assign.empty')}</p>
                ) : null}
                <Input
                  id="discrepancy-assignee"
                  inputMode="numeric"
                  value={assignee}
                  className="font-mono"
                  placeholder={t('reconciliation.case.assign.fallbackId')}
                  onChange={(event) => onAssigneeChange(event.target.value)}
                />
              </>
            ) : null}
          </div>
          <div className="space-y-2">
            <Label htmlFor="discrepancy-assign-reason">{t('reconciliation.enqueue.reason')}</Label>
            <Textarea
              id="discrepancy-assign-reason"
              value={reason}
              rows={3}
              onChange={(event) => onReasonChange(event.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" className="w-full sm:w-auto" onClick={() => onOpenChange(false)}>
            {t('withdrawals.actions.back')}
          </Button>
          <Button type="button" className="w-full sm:w-auto" loading={loading} disabled={!valid} onClick={onConfirm}>
            {t('reconciliation.case.assign.action')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
